import { useLayoutEffect, useRef } from 'react';

/**
 * WebKit bug: a scroll container mounted while the page is still settling never gets a
 * wheel/trackpad scroll region (keyboard scrolling still works). Hiding it for one frame
 * makes WebKit rebuild the region. That must run after the page settles, or the region is
 * rebuilt broken; style nudges that keep the element in layout don't fix it.
 *
 * Attach the returned ref (or pass your own) to the scroll container. WebKit-only.
 *   const scrollRef = useSafariScrollFix(undefined, hasRows);
 *   return <TableContainer ref={scrollRef} ...>...</TableContainer>;
 *
 * Pass `trigger` when the container mounts before its content (a loading frame): a heal
 * that runs while it's still empty doesn't stick, so each change re-arms it.
 *
 * Tunable for testing: ?settlems=NNN or localStorage 'safariScrollSettleMs'.
 */
const DEFAULT_SETTLE_MS = 250; // margin after the load event before healing
const MIN_SAFE_MS = 450; // never heal earlier than this since navigation, even on instant loads

const isAffectedWebKit = typeof navigator !== 'undefined'
  && /AppleWebKit/.test(navigator.userAgent)
  && !/Chrome|Chromium|Edg\//.test(navigator.userAgent);

function resolveSettleMs() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('settlems');
    if (fromUrl && Number.isFinite(parseInt(fromUrl, 10))) return parseInt(fromUrl, 10);
    const fromStore = window.localStorage.getItem('safariScrollSettleMs');
    if (fromStore && Number.isFinite(parseInt(fromStore, 10))) return parseInt(fromStore, 10);
  } catch (e) { /* ignore */
  }
  return DEFAULT_SETTLE_MS;
}

export default function useSafariScrollFix(externalRef, trigger) {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;

  useLayoutEffect(() => {
    if (!isAffectedWebKit) return undefined;

    const settleMs = resolveSettleMs();
    let timer = 0;
    let raf = 0;
    // Tracked so cleanup can restore it if torn down mid-toggle (unmount, StrictMode re-run).
    let hiddenEl = null;
    let prevDisplay = '';

    const restore = () => {
      if (hiddenEl) {
        hiddenEl.style.display = prevDisplay;
        hiddenEl = null;
      }
    };

    const heal = () => {
      const el = ref.current;
      if (!el) return;
      hiddenEl = el;
      prevDisplay = el.style.display;
      el.style.display = 'none';
      const flushed = el.offsetHeight; // read flushes layout so the detach actually applies
      if (flushed >= 0) {
        raf = window.requestAnimationFrame(restore);
      }
    };

    const schedule = () => {
      const delay = Math.max(settleMs, MIN_SAFE_MS - performance.now());
      timer = window.setTimeout(heal, delay);
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return () => {
      window.removeEventListener('load', schedule);
      if (timer) window.clearTimeout(timer);
      if (raf) window.cancelAnimationFrame(raf);
      restore();
    };
    // Re-run on `trigger` only; the ref object identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return ref;
}
